import { useState, useCallback, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { X, RotateCcw, Loader2, Plus, Trash2 } from 'lucide-react';
import { useSettingsStore } from '@/stores/settings-store';
import { useWorkspaceStore } from '@/stores/workspace-store';
import { apiClient } from '@/api/client';
import { DARK_EDITOR_THEMES, LIGHT_EDITOR_THEMES, DARK_TERMINAL_THEMES, LIGHT_TERMINAL_THEMES, DARK_GRID_THEMES, LIGHT_GRID_THEMES } from '@/config/themes';
import type { UserInfo, UserRole } from '@/types';

// Auth0 vs local-mode detection. Same env-var pattern App.tsx uses to
// decide which root component to mount; here it picks the right Users
// tab implementation (local-bcrypt vs Auth0 app_users directory).
const auth0Enabled = !!(import.meta.env.VITE_AUTH0_DOMAIN && import.meta.env.VITE_AUTH0_CLIENT_ID);

interface MeResponse {
  id: string;
  email?: string;
  username: string;
  displayName: string;
  role: UserRole;
}

// ═══════════════════════════════════════════
// Types
// ═══════════════════════════════════════════

interface SettingsModalProps {
  onClose: () => void;
}

type SettingsTab = 'editor' | 'terminal' | 'grid' | 'ui' | 'server' | 'users' | 'api-keys' | 'markdown';

function getTabs(role: UserRole | undefined): { id: SettingsTab; label: string }[] {
  const isAdmin = role === 'admin';
  const tabs: { id: SettingsTab; label: string }[] = [
    { id: 'editor', label: 'Editor' },
    { id: 'terminal', label: 'Terminal' },
    { id: 'grid', label: 'Data Grid' },
    { id: 'markdown', label: 'Markdown Viewer' },
    { id: 'ui', label: 'UI' },
  ];
  // Server settings affect everyone — admin-only.
  if (isAdmin) {
    tabs.push({ id: 'server', label: 'Server' });
  }
  tabs.push({ id: 'api-keys', label: 'API Keys' });
  if (isAdmin) {
    tabs.push({ id: 'users', label: 'Users' });
  }
  return tabs;
}

// ═══════════════════════════════════════════
// Setting field definitions
// ═══════════════════════════════════════════

interface SettingFieldDef {
  key: string;
  label: string;
  type: 'number' | 'text' | 'toggle' | 'select';
  defaultValue: string;
  options?: { value: string; label: string }[];
  min?: number;
  max?: number;
  step?: number;
  description?: string;
}

const EDITOR_FIELDS: SettingFieldDef[] = [
  { key: 'editor.theme.dark', label: 'Dark Mode Theme', type: 'select', defaultValue: 'dbgo-dark',
    options: DARK_EDITOR_THEMES.map(t => ({ value: t.id, label: t.name })) },
  { key: 'editor.theme.light', label: 'Light Mode Theme', type: 'select', defaultValue: 'dbgo-light',
    options: LIGHT_EDITOR_THEMES.map(t => ({ value: t.id, label: t.name })) },
  { key: 'editor.fontSize', label: 'Font Size', type: 'number', defaultValue: '12.5', min: 8, max: 32, step: 0.5 },
  { key: 'editor.fontFamily', label: 'Font Family', type: 'text', defaultValue: "'JetBrains Mono', monospace" },
  { key: 'editor.lineHeight', label: 'Line Height', type: 'number', defaultValue: '1.65', min: 1, max: 3, step: 0.05 },
  { key: 'editor.tabSize', label: 'Tab Size', type: 'number', defaultValue: '2', min: 1, max: 8, step: 1 },
  { key: 'editor.minimap', label: 'Minimap', type: 'toggle', defaultValue: 'false' },
  { key: 'editor.wordWrap', label: 'Word Wrap', type: 'select', defaultValue: 'on', options: [
    { value: 'on', label: 'On' },
    { value: 'off', label: 'Off' },
    { value: 'wordWrapColumn', label: 'At Column' },
    { value: 'bounded', label: 'Bounded' },
  ]},
  { key: 'editor.lineNumbers', label: 'Line Numbers', type: 'select', defaultValue: 'on', options: [
    { value: 'on', label: 'On' },
    { value: 'off', label: 'Off' },
    { value: 'relative', label: 'Relative' },
  ]},
];

const TERMINAL_FIELDS: SettingFieldDef[] = [
  { key: 'terminal.theme.dark', label: 'Dark Mode Theme', type: 'select', defaultValue: 'dbgo-dark',
    options: DARK_TERMINAL_THEMES.map(t => ({ value: t.id, label: t.name })) },
  { key: 'terminal.theme.light', label: 'Light Mode Theme', type: 'select', defaultValue: 'dbgo-light',
    options: LIGHT_TERMINAL_THEMES.map(t => ({ value: t.id, label: t.name })) },
  { key: 'terminal.fontSize', label: 'Font Size', type: 'number', defaultValue: '13', min: 8, max: 32, step: 1 },
  { key: 'terminal.fontFamily', label: 'Font Family', type: 'text', defaultValue: "'JetBrains Mono', monospace" },
  { key: 'terminal.cursorBlink', label: 'Cursor Blink', type: 'toggle', defaultValue: 'true' },
  { key: 'terminal.scrollback', label: 'Scrollback Lines', type: 'number', defaultValue: '10000', min: 100, max: 100000, step: 100 },
];

const GRID_FIELDS: SettingFieldDef[] = [
  { key: 'grid.theme.dark', label: 'Dark Mode Theme', type: 'select', defaultValue: 'dbgo-dark',
    options: DARK_GRID_THEMES.map(t => ({ value: t.id, label: t.name })) },
  { key: 'grid.theme.light', label: 'Light Mode Theme', type: 'select', defaultValue: 'dbgo-light',
    options: LIGHT_GRID_THEMES.map(t => ({ value: t.id, label: t.name })) },
  { key: 'grid.fontFamily', label: 'Font Family', type: 'text', defaultValue: "'JetBrains Mono', monospace" },
  { key: 'grid.fontSize', label: 'Font Size', type: 'number', defaultValue: '12', min: 8, max: 24, step: 1 },
  { key: 'grid.rowHeight', label: 'Row Height', type: 'number', defaultValue: '30', min: 20, max: 60, step: 1 },
  { key: 'grid.headerHeight', label: 'Header Height', type: 'number', defaultValue: '32', min: 20, max: 60, step: 1 },
  { key: 'grid.cellPadding', label: 'Cell Padding', type: 'number', defaultValue: '12', min: 4, max: 24, step: 1 },
];

const UI_FIELDS: SettingFieldDef[] = [
  { key: 'ui.fontFamily', label: 'Input Font Family', type: 'text', defaultValue: 'system-ui, -apple-system, sans-serif',
    description: 'Applied to form inputs (connection form, filter fields, modals, prompts).' },
  { key: 'ui.fontSize', label: 'Input Font Size (px)', type: 'number', defaultValue: '13', min: 11, max: 24, step: 1,
    description: 'On touch devices this is clamped to a minimum of 16px to prevent iOS Safari auto-zoom on focus.' },
  { key: 'ui.toastDuration', label: 'Toast Duration (ms)', type: 'number', defaultValue: '4000', min: 1000, max: 15000, step: 500 },
  { key: 'ui.stateSyncDebounce', label: 'State Sync Debounce (ms)', type: 'number', defaultValue: '1000', min: 200, max: 5000, step: 100 },
  { key: 'ui.schemaCacheTime', label: 'Schema Cache Time (ms)', type: 'number', defaultValue: '60000', min: 5000, max: 600000, step: 5000 },
  { key: 'ui.treeIndent', label: 'Tree Indent (px)', type: 'number', defaultValue: '12', min: 4, max: 32, step: 2 },
  { key: 'ui.wsReconnectDelay', label: 'WebSocket Reconnect Delay (ms)', type: 'number', defaultValue: '3000', min: 1000, max: 30000, step: 1000 },
  { key: 'search.columnResultCap', label: 'Column Search Result Cap', type: 'number', defaultValue: '200', min: 50, max: 5000, step: 50,
    description: 'Max columns the Cmd+P search walks across all cached schemas before scoring. Lift this if you have heavy-schema connections with 10k+ columns.' },
];

const SERVER_FIELDS: SettingFieldDef[] = [
  { key: 'server.connectionPoolSize', label: 'Connection Pool Size', type: 'number', defaultValue: '5', min: 1, max: 50, step: 1 },
  { key: 'server.connectionTimeout', label: 'Connection Timeout (ms)', type: 'number', defaultValue: '10000', min: 1000, max: 60000, step: 1000 },
  { key: 'server.idleTimeout', label: 'Idle Timeout (ms)', type: 'number', defaultValue: '30000', min: 5000, max: 300000, step: 5000 },
  { key: 'server.queryPageSize', label: 'Query Page Size', type: 'number', defaultValue: '100', min: 10, max: 10000, step: 10 },
  { key: 'server.keepaliveInterval', label: 'Keepalive Interval (ms)', type: 'number', defaultValue: '30000', min: 5000, max: 120000, step: 5000 },
  { key: 'server.terminalGracePeriod', label: 'Terminal Grace Period (ms)', type: 'number', defaultValue: '30000', min: 5000, max: 300000, step: 5000 },
  { key: 'server.maxLogEntries', label: 'Max Log Entries', type: 'number', defaultValue: '500', min: 50, max: 5000, step: 50 },
  // Admin shell: tmux session name on the DBGo server host that admins
  // can attach to from a pinned tab. Empty disables the feature.
  { key: 'server.adminShellTmuxSession', label: 'Admin Shell tmux session', type: 'text', defaultValue: 'dbgo',
    description: 'Pinned terminal tab for admins, attached to this tmux session on the DBGo host. Empty disables the feature.' },
];

const MARKDOWN_FIELDS: SettingFieldDef[] = [
  { key: 'markdown.codeTheme.dark', label: 'Code Block Theme (Dark)', type: 'select', defaultValue: 'dbgo-dark',
    options: DARK_EDITOR_THEMES.map((t) => ({ value: t.id, label: t.name })),
    description: 'Reuses the editor theme palette for syntax highlighting in markdown code blocks.' },
  { key: 'markdown.codeTheme.light', label: 'Code Block Theme (Light)', type: 'select', defaultValue: 'dbgo-light',
    options: LIGHT_EDITOR_THEMES.map((t) => ({ value: t.id, label: t.name })) },
  { key: 'markdown.fontSize', label: 'Body Font Size (px)', type: 'number', defaultValue: '13', min: 11, max: 22, step: 1 },
  { key: 'markdown.fontFamily', label: 'Body Font Family', type: 'text', defaultValue: 'system-ui, -apple-system, sans-serif',
    description: 'Sans-serif by default for prose. Use a serif (e.g. Georgia, Charter) if you prefer reading-style typography.' },
  { key: 'markdown.lineHeight', label: 'Line Height', type: 'number', defaultValue: '1.65', min: 1, max: 2.5, step: 0.05 },
  { key: 'markdown.maxWidth', label: 'Max Reading Width (px)', type: 'number', defaultValue: '720', min: 480, max: 1200, step: 20,
    description: 'Body content is centered within this width. ~720px (≈72 characters) is a common reading-comfort target.' },
  { key: 'markdown.codeFontSize', label: 'Code Font Size (px)', type: 'number', defaultValue: '12', min: 9, max: 20, step: 1 },
  { key: 'markdown.codeFontFamily', label: 'Code Font Family', type: 'text', defaultValue: "'JetBrains Mono', monospace" },
];

const FIELDS_BY_TAB: Record<string, SettingFieldDef[]> = {
  editor: EDITOR_FIELDS,
  terminal: TERMINAL_FIELDS,
  grid: GRID_FIELDS,
  ui: UI_FIELDS,
  server: SERVER_FIELDS,
  markdown: MARKDOWN_FIELDS,
};

function getDefaultsForTab(tab: SettingsTab): Record<string, string> {
  const fields = FIELDS_BY_TAB[tab];
  if (!fields) return {};
  const defaults: Record<string, string> = {};
  for (const field of fields) {
    defaults[field.key] = field.defaultValue;
  }
  return defaults;
}

// ═══════════════════════════════════════════
// Component
// ═══════════════════════════════════════════

export function SettingsModal({ onClose }: SettingsModalProps) {
  const [activeTab, setActiveTab] = useState<SettingsTab>('editor');
  const settingsStore = useSettingsStore();
  const [localChanges, setLocalChanges] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Role drives which tabs are visible (Server + Users are admin-only).
  // /api/auth/me returns the canonical role from app_users post-migration.
  const { data: me } = useQuery<MeResponse>({
    queryKey: ['me'],
    queryFn: () => apiClient.get<MeResponse>('/api/auth/me'),
    staleTime: 60_000,
  });
  const role = me?.role;
  const tabs = getTabs(role);

  // If the role finishes resolving on a non-admin tab the user shouldn't
  // see, fall back to the editor tab. Avoids leaving someone on Server
  // after a demote refresh.
  useEffect(() => {
    if (!tabs.some((t) => t.id === activeTab)) {
      setActiveTab('editor');
    }
  }, [tabs, activeTab]);

  // Merged view: store settings + local changes
  const getValue = useCallback((key: string, defaultValue: string): string => {
    if (key in localChanges) return localChanges[key];
    return settingsStore.settings[key] ?? defaultValue;
  }, [localChanges, settingsStore.settings]);

  const handleChange = useCallback((key: string, value: string) => {
    setLocalChanges((prev) => ({ ...prev, [key]: value }));
    setError('');
  }, []);

  const hasChanges = Object.keys(localChanges).length > 0;

  // Save all local changes
  const handleSave = useCallback(async () => {
    if (!hasChanges) return;
    setSaving(true);
    setError('');
    try {
      await settingsStore.save(localChanges);
      setLocalChanges({});
    } catch {
      setError('Failed to save settings');
    } finally {
      setSaving(false);
    }
  }, [hasChanges, localChanges, settingsStore]);

  // Reset current tab to defaults
  const handleResetTab = useCallback(async () => {
    const defaults = getDefaultsForTab(activeTab);
    if (Object.keys(defaults).length === 0) return;
    setSaving(true);
    setError('');
    try {
      await settingsStore.save(defaults);
      // Remove any local changes for these keys
      setLocalChanges((prev) => {
        const next = { ...prev };
        for (const key of Object.keys(defaults)) {
          delete next[key];
        }
        return next;
      });
    } catch {
      setError('Failed to reset settings');
    } finally {
      setSaving(false);
    }
  }, [activeTab, settingsStore]);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const fields = FIELDS_BY_TAB[activeTab];
  const isUsersTab = activeTab === 'users';
  const isApiKeysTab = activeTab === 'api-keys';
  const hasFooterActions = !isUsersTab && !isApiKeysTab;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div
        className="w-[580px] max-h-[80vh] flex flex-col bg-dbgo-modal border rounded-lg shadow-2xl animate-fade-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-4 pb-0">
          <h2 className="text-sm font-semibold text-text-primary">Settings</h2>
          <button
            type="button"
            onClick={onClose}
            className="w-6 h-6 flex items-center justify-center rounded-sm text-text-muted hover:text-text-secondary hover:bg-dbgo-tab-hover transition-colors"
          >
            <X size={14} />
          </button>
        </div>

        {/* Tab bar */}
        <div className="flex bg-dbgo-panel mx-5 mt-3 rounded-sm overflow-hidden">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`
                flex-1 text-xs font-medium py-2 transition-colors relative
                ${activeTab === tab.id
                  ? 'text-text-primary'
                  : 'text-text-muted hover:text-text-secondary'
                }
              `}
            >
              {tab.label}
              {activeTab === tab.id && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-accent-blue" />
              )}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div className="flex-1 overflow-y-auto px-5 py-4 min-h-0">
          {isUsersTab ? (
            // Auth0 mode: app_users directory with role + active toggle.
            // Local mode: legacy bcrypt user CRUD.
            auth0Enabled ? <AppUsersPanel meEmail={me?.email ?? me?.id ?? ''} /> : <UsersPanel />
          ) : isApiKeysTab ? (
            <ApiKeysPanel onClose={onClose} />
          ) : (
            <div className="flex flex-col gap-3">
              {fields?.map((field) => (
                <SettingField
                  key={field.key}
                  field={field}
                  value={getValue(field.key, field.defaultValue)}
                  onChange={(val) => handleChange(field.key, val)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Error */}
        {error && (
          <div className="px-5 pb-2 text-xs text-accent-red">{error}</div>
        )}

        {/* Footer */}
        {isUsersTab && (
          <div className="flex items-center justify-end px-5 py-3 border-t">
            <button
              type="button"
              onClick={() => { apiClient.logout(); }}
              className="flex items-center gap-1.5 px-3 py-[6px] text-xs font-medium text-accent-red border border-accent-red/30 rounded-sm hover:bg-accent-red/10 transition-colors"
            >
              Sign Out
            </button>
          </div>
        )}
        {hasFooterActions && (
          <div className="flex items-center justify-between px-5 py-3 border-t">
            <button
              type="button"
              onClick={handleResetTab}
              disabled={saving}
              className="flex items-center gap-1.5 px-3 py-[6px] text-xs font-medium text-text-secondary border rounded-sm hover:text-text-primary hover:bg-dbgo-tab-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <RotateCcw size={12} />
              Reset Tab Defaults
            </button>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onClose}
                disabled={saving}
                className="px-3 py-[6px] text-xs font-medium text-text-secondary hover:text-text-primary rounded-sm hover:bg-dbgo-tab-hover transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving || !hasChanges}
                className="px-4 py-[6px] text-xs font-medium text-white bg-accent-blue hover:bg-accent-blue/90 disabled:opacity-50 disabled:cursor-not-allowed rounded-sm transition-colors"
              >
                {saving ? (
                  <span className="flex items-center gap-1.5">
                    <Loader2 size={12} className="animate-spin" />
                    Saving...
                  </span>
                ) : (
                  'Save'
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════
// Users Panel (admin only)
// ═══════════════════════════════════════════

function UsersPanel() {
  const [users, setUsers] = useState<UserInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await apiClient.getUsers();
      setUsers(result);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleDelete = useCallback(async (user: UserInfo) => {
    if (!window.confirm(`Delete user "${user.username}"? This cannot be undone.`)) return;
    try {
      await apiClient.deleteUser(user.id);
      setUsers((prev) => prev.filter((u) => u.id !== user.id));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to delete user');
    }
  }, []);

  const handleRoleChange = useCallback(async (user: UserInfo, newRole: UserRole) => {
    try {
      const updated = await apiClient.updateUser(user.id, { role: newRole });
      setUsers((prev) => prev.map((u) => (u.id === user.id ? updated : u)));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to update role');
    }
  }, []);

  const currentUser = apiClient.getCurrentUser();

  if (loading) {
    return <div className="text-xs text-text-muted py-4 text-center">Loading users...</div>;
  }

  return (
    <div className="flex flex-col gap-3">
      {error && <div className="text-xs text-accent-red">{error}</div>}

      {/* User list */}
      <div className="border rounded-sm overflow-hidden">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-dbgo-panel text-text-secondary">
              <th className="text-left px-3 py-2 font-semibold">Username</th>
              <th className="text-left px-3 py-2 font-semibold">Display Name</th>
              <th className="text-left px-3 py-2 font-semibold">Role</th>
              <th className="text-right px-3 py-2 font-semibold w-16"></th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => {
              const isSelf = currentUser?.id === user.id;
              return (
                <tr key={user.id} className="border-t hover:bg-dbgo-panel/50 transition-colors">
                  <td className="px-3 py-2 text-text-primary font-medium">
                    {user.username}
                    {isSelf && <span className="text-text-muted ml-1">(you)</span>}
                  </td>
                  <td className="px-3 py-2 text-text-secondary">{user.displayName}</td>
                  <td className="px-3 py-2">
                    {isSelf ? (
                      <span className="text-text-secondary">{user.role}</span>
                    ) : (
                      <select
                        value={user.role}
                        onChange={(e) => handleRoleChange(user, e.target.value as UserRole)}
                        className="bg-dbgo-input border rounded-sm px-1.5 py-0.5 text-xs text-text-primary outline-none focus:border-focus transition-colors"
                      >
                        <option value="admin">admin</option>
                        <option value="editor">editor</option>
                        <option value="viewer">viewer</option>
                      </select>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {!isSelf && (
                      <button
                        type="button"
                        onClick={() => handleDelete(user)}
                        className="text-text-muted hover:text-accent-red transition-colors p-1"
                        title="Delete user"
                      >
                        <Trash2 size={12} />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Add user */}
      {showAddForm ? (
        <AddUserForm
          onCreated={(user) => {
            setUsers((prev) => [...prev, user]);
            setShowAddForm(false);
          }}
          onCancel={() => setShowAddForm(false)}
        />
      ) : (
        <button
          type="button"
          onClick={() => setShowAddForm(true)}
          className="flex items-center gap-1.5 px-3 py-[6px] text-xs font-medium text-text-secondary border rounded-sm hover:text-text-primary hover:bg-dbgo-tab-hover transition-colors self-start"
        >
          <Plus size={12} />
          Add User
        </button>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════
// Add User Form
// ═══════════════════════════════════════════

interface AddUserFormProps {
  onCreated: (user: UserInfo) => void;
  onCancel: () => void;
}

function AddUserForm({ onCreated, onCancel }: AddUserFormProps) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState<string>('editor');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username.trim() || !password.trim()) return;

    setSaving(true);
    setError('');
    try {
      const user = await apiClient.createUser({
        username: username.trim(),
        password,
        displayName: displayName.trim() || undefined,
        role,
      });
      onCreated(user);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to create user');
    } finally {
      setSaving(false);
    }
  }, [username, password, displayName, role, onCreated]);

  return (
    <form onSubmit={handleSubmit} className="border rounded-sm p-3 flex flex-col gap-2">
      <div className="text-xs font-semibold text-text-primary mb-1">New User</div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="block text-xs text-text-secondary mb-1">Username</label>
          <input
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="username"
            autoFocus
            disabled={saving}
            className="w-full bg-dbgo-input border rounded-sm px-2 py-1 text-xs text-text-primary placeholder:text-text-muted outline-none focus:border-focus transition-colors"
          />
        </div>
        <div>
          <label className="block text-xs text-text-secondary mb-1">Display Name</label>
          <input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="(optional)"
            disabled={saving}
            className="w-full bg-dbgo-input border rounded-sm px-2 py-1 text-xs text-text-primary placeholder:text-text-muted outline-none focus:border-focus transition-colors"
          />
        </div>
        <div>
          <label className="block text-xs text-text-secondary mb-1">Password</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="min 4 chars"
            disabled={saving}
            className="w-full bg-dbgo-input border rounded-sm px-2 py-1 text-xs text-text-primary placeholder:text-text-muted outline-none focus:border-focus transition-colors"
          />
        </div>
        <div>
          <label className="block text-xs text-text-secondary mb-1">Role</label>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value)}
            disabled={saving}
            className="w-full bg-dbgo-input border rounded-sm px-2 py-1 text-xs text-text-primary outline-none focus:border-focus transition-colors"
          >
            <option value="admin">admin</option>
            <option value="editor">editor</option>
            <option value="viewer">viewer</option>
          </select>
        </div>
      </div>

      {error && <div className="text-xs text-accent-red">{error}</div>}

      <div className="flex items-center gap-2 mt-1">
        <button
          type="submit"
          disabled={saving || !username.trim() || !password.trim()}
          className="px-3 py-[5px] text-xs font-medium text-white bg-accent-blue hover:bg-accent-blue/90 disabled:opacity-50 disabled:cursor-not-allowed rounded-sm transition-colors"
        >
          {saving ? 'Creating...' : 'Create User'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="px-3 py-[5px] text-xs font-medium text-text-secondary hover:text-text-primary rounded-sm hover:bg-dbgo-tab-hover transition-colors"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

// ═══════════════════════════════════════════
// Shared form primitives
// ═══════════════════════════════════════════

const inputClass =
  'w-full bg-dbgo-input border rounded-sm px-2.5 py-[7px] text-[13px] text-text-primary placeholder:text-text-muted outline-none focus:border-focus transition-colors';

const selectClass =
  'w-full bg-dbgo-input border rounded-sm px-2.5 py-[7px] text-[13px] text-text-primary outline-none focus:border-focus transition-colors appearance-none cursor-pointer';

const labelClass = 'block text-xs font-semibold text-text-secondary mb-1.5';

// ═══════════════════════════════════════════
// Setting field renderer
// ═══════════════════════════════════════════

interface SettingFieldProps {
  field: SettingFieldDef;
  value: string;
  onChange: (value: string) => void;
}

function SettingField({ field, value, onChange }: SettingFieldProps) {
  if (field.type === 'toggle') {
    const checked = value === 'true';
    return (
      <div className="flex items-center justify-between py-1">
        <label className="text-xs font-semibold text-text-secondary">{field.label}</label>
        <button
          type="button"
          role="switch"
          aria-checked={checked}
          onClick={() => onChange(checked ? 'false' : 'true')}
          className={`
            relative w-9 h-5 rounded-full transition-colors flex-shrink-0
            ${checked ? 'bg-accent-blue' : 'bg-dbgo-input border'}
          `}
        >
          <span
            className={`
              absolute top-[3px] w-3.5 h-3.5 rounded-full bg-white transition-transform
              ${checked ? 'translate-x-[19px]' : 'translate-x-[3px]'}
            `}
          />
        </button>
      </div>
    );
  }

  if (field.type === 'select') {
    return (
      <div>
        <label className={labelClass}>{field.label}</label>
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={selectClass}
        >
          {field.options?.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>
    );
  }

  if (field.type === 'number') {
    return (
      <div>
        <label className={labelClass}>{field.label}</label>
        <input
          type="number"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          min={field.min}
          max={field.max}
          step={field.step}
          className={inputClass}
        />
        {field.description && (
          <p className="text-[11px] text-text-muted mt-1">{field.description}</p>
        )}
      </div>
    );
  }

  // text
  return (
    <div>
      <label className={labelClass}>{field.label}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={inputClass}
      />
      {field.description && (
        <p className="text-[11px] text-text-muted mt-1">{field.description}</p>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════
// API Keys Panel
// ═══════════════════════════════════════════
//
// UI for generating API keys that Claude skills (and any programmatic
// consumer) can use against DBGo. See docs/api-keys.md for the auth flow
// and scope taxonomy.
//
// Design notes:
// - The raw key is returned exactly once on creation. We render it in a
//   full-width "save this now" banner with a Copy button. Navigating away
//   destroys it forever — this is enforced by the server (hashed storage).
// - Revoke is soft-delete so historical audit log entries can still
//   resolve key_id → name.

interface ApiKeyPublic {
  id: string;
  name: string;
  display: string;
  scopes: string[];
  connectionIds: string[] | null;
  createdAt: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
}

function ApiKeysPanel({ onClose }: { onClose: () => void }) {
  const openMarkdownTab = useCallback(() => {
    useWorkspaceStore.getState().openTab({
      type: 'markdown',
      id: 'markdown:docs/api-keys.md',
      path: 'docs/api-keys.md',
      title: 'API Keys',
    });
    onClose();
  }, [onClose]);

  const [keys, setKeys] = useState<ApiKeyPublic[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [fresh, setFresh] = useState<{ row: ApiKeyPublic; rawKey: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await apiClient.get<ApiKeyPublic[]>('/api/keys');
      setKeys(result);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load keys');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleRevoke = useCallback(async (key: ApiKeyPublic) => {
    if (!window.confirm(`Revoke key "${key.name}"? This cannot be undone.`)) return;
    try {
      await apiClient.delete(`/api/keys/${key.id}`);
      setKeys((prev) => prev.map((k) => (k.id === key.id ? { ...k, revokedAt: new Date().toISOString() } : k)));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to revoke key');
    }
  }, []);

  const activeKeys = keys.filter((k) => !k.revokedAt);
  const revokedKeys = keys.filter((k) => k.revokedAt);

  return (
    <div className="flex flex-col gap-3">
      {/* Freshly-minted key banner — shown once, then cleared on close/new-create */}
      {fresh && (
        <FreshKeyBanner
          rawKey={fresh.rawKey}
          row={fresh.row}
          onDismiss={() => { setFresh(null); load(); }}
        />
      )}

      {/* Header with count + New button */}
      <div className="flex items-center justify-between">
        <div className="text-xs text-text-secondary">
          API keys let Claude, scripts, or dashboards talk to DBGo. They carry scoped access —
          see{' '}
          <button
            type="button"
            onClick={openMarkdownTab}
            className="text-accent-blue hover:underline"
          >
            docs/api-keys.md
          </button>.
        </div>
        <button
          type="button"
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-white bg-accent-blue hover:bg-accent-blue/90 rounded-sm transition-colors"
        >
          <Plus size={12} /> New Key
        </button>
      </div>

      {/* Connection instructions — collapsible "How to connect" panel */}
      <ConnectInstructions />


      {/* Create form */}
      {showCreate && (
        <CreateKeyForm
          onCancel={() => setShowCreate(false)}
          onCreated={(result) => {
            setShowCreate(false);
            setFresh(result);
          }}
          onError={(msg) => setError(msg)}
        />
      )}

      {/* Error */}
      {error && (
        <div className="text-xs text-accent-red bg-accent-red/10 border border-accent-red/20 rounded-sm px-3 py-2">{error}</div>
      )}

      {/* Key list */}
      {loading ? (
        <div className="text-xs text-text-muted">Loading…</div>
      ) : keys.length === 0 ? (
        <div className="text-xs text-text-muted italic">No API keys yet. Create one to get started.</div>
      ) : (
        <>
          {activeKeys.length > 0 && (
            <div className="flex flex-col gap-2">
              {activeKeys.map((k) => (
                <ApiKeyRow key={k.id} row={k} onRevoke={() => handleRevoke(k)} />
              ))}
            </div>
          )}
          {revokedKeys.length > 0 && (
            <div className="mt-2">
              <div className="text-2xs uppercase tracking-widest text-text-muted mb-1">Revoked</div>
              <div className="flex flex-col gap-1 opacity-60">
                {revokedKeys.map((k) => <ApiKeyRow key={k.id} row={k} onRevoke={() => { /* noop */ }} />)}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ApiKeyRow({ row, onRevoke }: { row: ApiKeyPublic; onRevoke: () => void }) {
  return (
    <div className="flex items-start gap-3 px-3 py-2 bg-dbgo-panel border rounded-sm">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-text-primary truncate">{row.name}</span>
          <code className="text-2xs font-mono text-text-muted bg-dbgo-badge px-1.5 py-[1px] rounded-sm">{row.display}</code>
        </div>
        <div className="flex flex-wrap gap-1 mt-1">
          {row.scopes.map((s) => (
            <span key={s} className="text-2xs font-mono text-accent-blue bg-accent-blue/10 px-1.5 py-[1px] rounded-sm">{s}</span>
          ))}
          {row.connectionIds && (
            <span className="text-2xs text-text-muted bg-dbgo-badge px-1.5 py-[1px] rounded-sm">
              {row.connectionIds.length} connection{row.connectionIds.length === 1 ? '' : 's'}
            </span>
          )}
        </div>
        <div className="text-2xs text-text-muted mt-1">
          Created {new Date(row.createdAt).toLocaleDateString()} ·{' '}
          {row.lastUsedAt ? `Last used ${new Date(row.lastUsedAt).toLocaleDateString()}` : 'Never used'}
        </div>
      </div>
      {!row.revokedAt && (
        <button
          type="button"
          onClick={onRevoke}
          className="flex-shrink-0 text-2xs text-accent-red hover:underline"
          title="Revoke this key"
        >
          <Trash2 size={13} />
        </button>
      )}
    </div>
  );
}

function FreshKeyBanner({ rawKey, row, onDismiss }: { rawKey: string; row: ApiKeyPublic; onDismiss: () => void }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(rawKey).catch(() => { /* ignore */ });
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="border-2 border-accent-yellow/50 bg-accent-yellow/5 rounded-sm p-3">
      <div className="flex items-start justify-between mb-2">
        <div>
          <div className="text-xs font-semibold text-text-primary">Save this key now — it won't be shown again</div>
          <div className="text-2xs text-text-muted">Key: <span className="font-medium">{row.name}</span></div>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          className="text-2xs text-text-muted hover:text-text-secondary"
        >
          I've saved it ✓
        </button>
      </div>
      <div className="flex items-center gap-2">
        <code className="flex-1 font-mono text-[11px] text-text-primary bg-dbgo-modal px-2 py-1.5 rounded-sm border break-all">{rawKey}</code>
        <button
          type="button"
          onClick={handleCopy}
          className="px-2.5 py-1.5 text-xs font-medium text-white bg-accent-blue hover:bg-accent-blue/90 rounded-sm"
        >
          {copied ? 'Copied!' : 'Copy'}
        </button>
      </div>
    </div>
  );
}

interface ScopePresets {
  presets: Record<string, string[]>;
  all: string[];
}

function CreateKeyForm({
  onCancel,
  onCreated,
  onError,
}: {
  onCancel: () => void;
  onCreated: (result: { row: ApiKeyPublic; rawKey: string }) => void;
  onError: (msg: string) => void;
}) {
  const [name, setName] = useState('');
  const [preset, setPreset] = useState<string>('Read-only (Claude safe)');
  const [scopes, setScopes] = useState<string[]>([]);
  const [presets, setPresets] = useState<ScopePresets | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    apiClient.get<ScopePresets>('/api/keys/scope-presets')
      .then((p) => {
        setPresets(p);
        setScopes(p.presets['Read-only (Claude safe)'] ?? []);
      })
      .catch(() => { /* leave defaults */ });
  }, []);

  const handlePreset = (name: string) => {
    setPreset(name);
    if (presets?.presets[name]) setScopes([...presets.presets[name]]);
  };

  const toggleScope = (s: string) => {
    setPreset('Custom');
    setScopes((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));
  };

  const handleSubmit = async () => {
    if (!name.trim() || scopes.length === 0) return;
    setSubmitting(true);
    try {
      const result = await apiClient.post<{ key: ApiKeyPublic; rawKey: string }>('/api/keys', {
        name: name.trim(),
        scopes,
      });
      onCreated({ row: result.key, rawKey: result.rawKey });
    } catch (err: unknown) {
      onError(err instanceof Error ? err.message : 'Failed to create key');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col gap-3 px-3 py-3 bg-dbgo-panel border rounded-sm">
      <div>
        <label className="block text-2xs uppercase tracking-widest text-text-muted mb-1">Name</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Claude Cowork"
          className="w-full px-2 py-1.5 text-xs bg-dbgo-input border rounded-sm text-text-primary outline-none focus:border-accent-blue"
        />
      </div>

      <div>
        <label className="block text-2xs uppercase tracking-widest text-text-muted mb-1">Preset</label>
        <div className="flex flex-wrap gap-1">
          {presets && Object.keys(presets.presets).map((name) => (
            <button
              key={name}
              type="button"
              onClick={() => handlePreset(name)}
              className={`px-2 py-1 text-2xs rounded-sm transition-colors ${
                preset === name
                  ? 'bg-accent-blue text-white'
                  : 'bg-dbgo-badge text-text-secondary hover:bg-dbgo-tab-hover'
              }`}
            >
              {name}
            </button>
          ))}
          <button
            type="button"
            onClick={() => setPreset('Custom')}
            className={`px-2 py-1 text-2xs rounded-sm transition-colors ${
              preset === 'Custom'
                ? 'bg-accent-blue text-white'
                : 'bg-dbgo-badge text-text-secondary hover:bg-dbgo-tab-hover'
            }`}
          >
            Custom
          </button>
        </div>
      </div>

      <div>
        <label className="block text-2xs uppercase tracking-widest text-text-muted mb-1">Scopes ({scopes.length} selected)</label>
        <div className="flex flex-wrap gap-1">
          {presets?.all.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => toggleScope(s)}
              className={`px-1.5 py-0.5 text-2xs font-mono rounded-sm transition-colors ${
                scopes.includes(s)
                  ? 'bg-accent-blue/15 text-accent-blue border border-accent-blue/30'
                  : 'bg-dbgo-badge text-text-muted hover:text-text-secondary border border-transparent'
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-end gap-2 mt-1">
        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
          className="px-3 py-1 text-xs text-text-secondary hover:text-text-primary rounded-sm hover:bg-dbgo-tab-hover"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={submitting || !name.trim() || scopes.length === 0}
          className="px-3 py-1 text-xs font-medium text-white bg-accent-blue hover:bg-accent-blue/90 disabled:opacity-50 rounded-sm"
        >
          {submitting ? 'Creating…' : 'Create Key'}
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════
// Connection instructions panel
// ═══════════════════════════════════════════
//
// Shows three integration paths side by side: Claude.ai custom connectors
// (via /mcp), Claude Code skill (via the markdown template), and raw curl
// (for scripts / one-off CLI use). Collapsible — keeps the API Keys tab
// focused on key management for users who already know the setup.

function ConnectInstructions() {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<'mcp' | 'skill' | 'curl'>('mcp');

  // Best-guess for the URL the user will paste — falls back to the
  // current origin since we don't know what's exposed publicly.
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://your-dbgo';
  const mcpUrl = `${origin}/mcp`;

  return (
    <div className="border rounded-sm bg-dbgo-panel">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-2 text-xs text-text-secondary hover:text-text-primary"
      >
        <span className="font-semibold">How to connect Claude</span>
        <span className="text-2xs text-text-muted">{open ? '▾ Hide' : '▸ Show'}</span>
      </button>

      {open && (
        <div className="border-t px-3 pb-3 pt-2">
          {/* Sub-tabs */}
          <div className="flex gap-1 mb-3">
            <ConnectTabButton active={tab === 'mcp'} onClick={() => setTab('mcp')}>
              Claude.ai (MCP) <span className="ml-1 text-2xs text-accent-blue/80">native</span>
            </ConnectTabButton>
            <ConnectTabButton active={tab === 'skill'} onClick={() => setTab('skill')}>
              Claude Code (skill)
            </ConnectTabButton>
            <ConnectTabButton active={tab === 'curl'} onClick={() => setTab('curl')}>
              curl / scripts
            </ConnectTabButton>
          </div>

          {tab === 'mcp' && (
            <div className="text-xs text-text-secondary space-y-2">
              <p>
                The cleanest path. Claude.ai's web and desktop clients support MCP custom
                connectors out of the box — paste a URL + bearer token and the tools
                auto-discover.
              </p>
              <ol className="list-decimal pl-5 space-y-1 text-xs">
                <li>Open <span className="font-mono">Claude.ai → Settings → Connectors</span> (or Claude Desktop's MCP settings).</li>
                <li>
                  Add a new <em>custom connector</em> with:
                  <div className="mt-1 ml-3 font-mono text-2xs bg-dbgo-modal border rounded-sm p-2 space-y-0.5">
                    <div><span className="text-text-muted">URL:</span> <CopyChip text={mcpUrl} /></div>
                    <div><span className="text-text-muted">Auth:</span> Bearer Token</div>
                    <div><span className="text-text-muted">Token:</span> your DBGo API key (<span className="font-mono text-text-muted">dbgo_live_…</span>)</div>
                  </div>
                </li>
                <li>Save. Tools become available in any conversation: <span className="font-mono text-2xs">list_connections</span>, <span className="font-mono text-2xs">query</span>, <span className="font-mono text-2xs">mutate</span>, <span className="font-mono text-2xs">ddl</span>, <span className="font-mono text-2xs">confirm</span>, <span className="font-mono text-2xs">rows_list</span>, <span className="font-mono text-2xs">rows_count</span>, <span className="font-mono text-2xs">get_schema</span>, <span className="font-mono text-2xs">get_all_schemas</span>.</li>
              </ol>
              <p className="text-2xs text-text-muted">
                Same scopes, same audit log, same two-step confirmation for destructive ops as the curl flow.
              </p>
            </div>
          )}

          {tab === 'skill' && (
            <div className="text-xs text-text-secondary space-y-2">
              <p>For Claude Code (CLI / VS Code / Cursor extensions). Drop a small skill markdown into your Claude config.</p>
              <ol className="list-decimal pl-5 space-y-1 text-xs">
                <li>Copy the skill template from <span className="font-mono">claude-skill/dbgo.md</span> into <span className="font-mono">~/.claude/skills/dbgo/dbgo.md</span>.</li>
                <li>
                  Set environment variables in your shell:
                  <pre className="mt-1 ml-3 bg-dbgo-modal border rounded-sm p-2 text-2xs font-mono overflow-x-auto">{`export DBGO_URL=${origin}
export DBGO_API_KEY=dbgo_live_...`}</pre>
                </li>
                <li>Open a Claude Code session — the skill auto-loads. Ask <em>"what database connections can you see?"</em> to verify.</li>
              </ol>
            </div>
          )}

          {tab === 'curl' && (
            <div className="text-xs text-text-secondary space-y-2">
              <p>For one-off CLI use, dashboards, or any HTTP client.</p>
              <pre className="bg-dbgo-modal border rounded-sm p-2 text-2xs font-mono overflow-x-auto">{`# List connections
curl -sH "Authorization: Bearer $DBGO_API_KEY" \\
  ${origin}/api/commands/connections

# Run a SELECT
curl -sH "Authorization: Bearer $DBGO_API_KEY" \\
  -H "Content-Type: application/json" \\
  -X POST ${origin}/api/commands/query \\
  -d '{"connectionId":"...","sql":"SELECT 1"}'`}</pre>
              <p className="text-2xs text-text-muted">Full reference: <span className="font-mono">docs/api-keys.md</span> § Gate summary.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ConnectTabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-2.5 py-1 text-2xs rounded-sm transition-colors ${
        active
          ? 'bg-accent-blue text-white'
          : 'bg-dbgo-badge text-text-secondary hover:bg-dbgo-tab-hover'
      }`}
    >
      {children}
    </button>
  );
}

function CopyChip({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const handle = () => {
    navigator.clipboard.writeText(text).catch(() => { /* ignore */ });
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  };
  return (
    <button
      type="button"
      onClick={handle}
      className="font-mono text-2xs hover:text-text-primary text-text-secondary"
      title="Copy to clipboard"
    >
      {text} <span className="text-accent-blue">{copied ? '✓ copied' : '⧉'}</span>
    </button>
  );
}

// ═══════════════════════════════════════════
// Auth0-mode Users Panel
// ═══════════════════════════════════════════

interface AppUser {
  email: string;
  auth0Sub: string | null;
  displayName: string | null;
  role: 'admin' | 'user';
  active: boolean;
  firstSeenAt: string;
  lastSeenAt: string;
}

/**
 * Admin-only Auth0-mode users tab. Lists everyone who has logged into
 * DBGo (one row per email in `app_users`) and lets the admin promote /
 * demote between admin and user, and grant / revoke DBGo access.
 *
 * Auth0 still gates whether the user can sign in at all (dbgo:access
 * permission). The "Active" toggle here is the second gate — DBGo
 * itself can refuse a user even if Auth0 still admits them.
 */
function AppUsersPanel({ meEmail }: { meEmail: string }) {
  const queryClient = useQueryClient();
  const { data: users = [], isLoading, error, refetch } = useQuery<AppUser[]>({
    queryKey: ['admin-users'],
    queryFn: () => apiClient.get<AppUser[]>('/api/admin/users'),
    staleTime: 10_000,
  });
  const [busy, setBusy] = useState<string | null>(null);
  const [errMsg, setErrMsg] = useState('');

  const patch = useCallback(async (email: string, body: { role?: 'admin' | 'user'; active?: boolean }) => {
    setBusy(email);
    setErrMsg('');
    try {
      // apiClient.put writes the body — same shape works for PATCH.
      await fetch(`/api/admin/users/${encodeURIComponent(email)}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('dbgo_token') ?? ''}`,
        },
        body: JSON.stringify(body),
      }).then(async (r) => {
        if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error ?? 'Update failed');
      });
      await refetch();
      if (email === meEmail && body.role) {
        queryClient.invalidateQueries({ queryKey: ['me'] });
      }
    } catch (err: unknown) {
      setErrMsg(err instanceof Error ? err.message : 'Update failed');
    } finally {
      setBusy(null);
    }
  }, [refetch, queryClient, meEmail]);

  if (isLoading) {
    return <div className="text-xs text-text-muted py-4 text-center">Loading users...</div>;
  }

  if (error) {
    return <div className="text-xs text-accent-red">Failed to load users: {error instanceof Error ? error.message : String(error)}</div>;
  }

  return (
    <div className="flex flex-col gap-3">
      {errMsg && <div className="text-xs text-accent-red">{errMsg}</div>}

      <div className="border rounded-sm overflow-hidden">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-dbgo-panel text-text-secondary">
              <th className="text-left px-3 py-2 font-semibold">Email</th>
              <th className="text-left px-3 py-2 font-semibold">Display Name</th>
              <th className="text-left px-3 py-2 font-semibold">Role</th>
              <th className="text-left px-3 py-2 font-semibold">Access</th>
              <th className="text-left px-3 py-2 font-semibold">Last Seen</th>
              <th className="text-right px-3 py-2 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => {
              const isMe = u.email === meEmail;
              const isAdmin = u.role === 'admin';
              const isActive = u.active;
              const rowBusy = busy === u.email;
              return (
                <tr key={u.email} className="border-t border-subtle">
                  <td className="px-3 py-2 text-text-primary">
                    {u.email}
                    {isMe && <span className="ml-2 text-text-muted">(you)</span>}
                  </td>
                  <td className="px-3 py-2 text-text-secondary">{u.displayName ?? '—'}</td>
                  <td className="px-3 py-2">
                    <span className={isAdmin ? 'text-accent-blue font-medium' : 'text-text-secondary'}>
                      {u.role}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <span className={isActive ? 'text-accent-green' : 'text-accent-red'}>
                      {isActive ? 'Active' : 'Revoked'}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-text-muted">
                    {u.lastSeenAt.replace('T', ' ').slice(0, 16)}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button
                      type="button"
                      disabled={rowBusy}
                      onClick={() => patch(u.email, { role: isAdmin ? 'user' : 'admin' })}
                      className="px-2 py-1 text-xs text-accent-blue hover:bg-accent-blue/10 rounded-sm disabled:opacity-40"
                    >
                      {isAdmin ? 'Demote' : 'Promote'}
                    </button>
                    <button
                      type="button"
                      disabled={rowBusy || isMe}
                      title={isMe ? "Can't revoke your own access" : undefined}
                      onClick={() => patch(u.email, { active: !isActive })}
                      className={`ml-1 px-2 py-1 text-xs rounded-sm disabled:opacity-40 ${
                        isActive
                          ? 'text-accent-red hover:bg-accent-red/10'
                          : 'text-accent-green hover:bg-accent-green/10'
                      }`}
                    >
                      {isActive ? 'Revoke' : 'Grant'}
                    </button>
                  </td>
                </tr>
              );
            })}
            {users.length === 0 && (
              <tr>
                <td colSpan={6} className="px-3 py-4 text-text-muted italic text-center">
                  No users yet. They appear here after first login.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="text-xs text-text-muted">
        Users appear here only after they log in once. <span className="text-text-secondary">Auth0 still gates initial sign-in via the dbgo:access permission</span> — revoking access here is an additional layer DBGo enforces on every request.
      </div>
    </div>
  );
}

